/**
 * Knowledge promotion: items with their checks and gates, editing, Check, promote, stop, auto-send,
 * try-it, and the runs that create items from an article or from imported pairs.
 */

import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { withBuScope } from '@/lib/business-unit-scope';
import type { ApiResponse } from '@/types';
import type {
	ImportPreview,
	KnowledgeArticle,
	KnowledgeEdit,
	KnowledgeItem,
	KnowledgeJob,
	KnowledgeTryResult
} from '@/modules/knowledge-promotion/types';

type BuScope = { bu_id?: string };
const E = API_ENDPOINTS.KNOWLEDGE_PROMOTION;

export const knowledgePromotionService = {
	async list(scope: BuScope): Promise<KnowledgeItem[]> {
		const response = await api.get<ApiResponse<{ items: KnowledgeItem[] }>>(E.ITEMS, { params: scope });
		return response.data.data.items;
	},

	async edit(id: string, edit: KnowledgeEdit): Promise<KnowledgeItem> {
		const response = await api.patch<ApiResponse<KnowledgeItem>>(E.ITEM(id), edit);
		return response.data.data;
	},

	async remove(id: string): Promise<void> {
		await api.delete(E.ITEM(id));
	},

	/** Drops a promoted item's edit waiting for Check; the served version stays. */
	async discardPending(id: string): Promise<KnowledgeItem> {
		const response = await api.delete<ApiResponse<KnowledgeItem>>(E.PENDING(id));
		return response.data.data;
	},

	async check(id: string): Promise<KnowledgeItem> {
		const response = await api.post<ApiResponse<KnowledgeItem>>(E.CHECK(id));
		return response.data.data;
	},

	async promote(id: string): Promise<KnowledgeItem> {
		const response = await api.post<ApiResponse<KnowledgeItem>>(E.PROMOTE(id));
		return response.data.data;
	},

	async stop(id: string): Promise<KnowledgeItem> {
		const response = await api.post<ApiResponse<KnowledgeItem>>(E.STOP(id));
		return response.data.data;
	},

	/** On only once earned: the server answers 409 with the reason otherwise. */
	async setAutoSend(id: string, autoSend: boolean): Promise<KnowledgeItem> {
		const response = await api.put<ApiResponse<KnowledgeItem>>(E.AUTO_SEND(id), { autoSend });
		return response.data.data;
	},

	async tryQuestion(question: string, itemId: string | null, scope: BuScope): Promise<KnowledgeTryResult> {
		const response = await api.post<ApiResponse<KnowledgeTryResult>>(withBuScope(E.TRY, scope), { question, itemId });
		return response.data.data;
	},

	async articles(scope: BuScope): Promise<KnowledgeArticle[]> {
		const response = await api.get<ApiResponse<{ articles: KnowledgeArticle[] }>>(E.ARTICLES, { params: scope });
		return response.data.data.articles;
	},

	/** replace: Regenerate, the source's existing questions and answers are removed and made again. */
	async generate(articleId: string, scope: BuScope, replace = false): Promise<KnowledgeJob> {
		const response = await api.post<ApiResponse<KnowledgeJob>>(withBuScope(E.GENERATE, scope), {
			articleId,
			...(replace ? { replace: true } : {})
		});
		return response.data.data;
	},

	async previewImport(text: string): Promise<ImportPreview> {
		const response = await api.post<ApiResponse<ImportPreview>>(E.IMPORT_PREVIEW, { text });
		return response.data.data;
	},

	async importPairs(text: string, scope: BuScope): Promise<{ job: KnowledgeJob; skippedRows: string[] }> {
		const response = await api.post<ApiResponse<{ job: KnowledgeJob; skippedRows: string[] }>>(withBuScope(E.IMPORT, scope), { text });
		return response.data.data;
	},

	async jobs(): Promise<KnowledgeJob[]> {
		const response = await api.get<ApiResponse<{ jobs: KnowledgeJob[] }>>(E.JOBS);
		return response.data.data.jobs;
	}
};
